import React from 'react';
import { ActivityIndicator, Platform, StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import { Link } from 'expo-router';
import { FontAwesome6 } from '@expo/vector-icons';
import type { FeedPaper } from '../../lib/hooks/useProjectFeed';
import colors from '../../lib/colors';
import { interRegular, interSemiBold } from '../../lib/fonts';
import PaperCard from '../papers/PaperCard';
import { PaperFeedButton } from '../ui/Button';
import PreviewTrackingCard from '../billing/PreviewTrackingCard';
import PreviewResumeTrackingButton from '../billing/PreviewResumeTrackingButton';
import PreviewHiddenMatchesNotice from '../billing/PreviewHiddenMatchesNotice';
import { getFeedPaperHref } from '../feed/projectFeedUi';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';

interface ProjectPreviewReadySectionProps {
    projectId: string;
    projectName: string;
    pathname: string;
    feedHref: string;
    resultDigestId?: string | null;
    totalFoundCount: number;
    papers: FeedPaper[];
    isLoading?: boolean;
    hasLoadError?: boolean;
    trackingState?: 'pausing' | 'paused';
}

export default function ProjectPreviewReadySection({
    projectId,
    projectName,
    pathname,
    feedHref,
    resultDigestId,
    totalFoundCount,
    papers,
    isLoading = false,
    hasLoadError = false,
    trackingState = 'paused',
}: ProjectPreviewReadySectionProps) {
    const { i18n } = useLingui();
    const { width } = useWindowDimensions();
    const isNarrow = width < 600;
    const shownCount = papers.length;
    const hasPapers = shownCount > 0;
    const foundLabel = i18n._({
        ...msg({
        id: 'preview.paperCount',
        message: '{count, plural, one {# paper} other {# papers}}',
        }),
        values: { count: totalFoundCount },
    });
    const previewLabel = i18n._({
        ...msg({
        id: 'preview.visiblePaperCount',
        message: '{count, plural, one {# paper} other {# papers}}',
        }),
        values: { count: shownCount },
    });
    const previewScopeLabel = totalFoundCount > shownCount
        ? i18n._({
            ...msg({
            id: 'preview.topPaperCount',
            message: 'the top {previewLabel}',
            }),
            values: { previewLabel },
        })
        : previewLabel;
    const isPausing = trackingState === 'pausing';
    const stateTitle = hasPapers && !hasLoadError
        ? i18n._(msg({ id: 'preview.ready.title', message: 'Your first matching papers are ready.' }))
        : isPausing
            ? i18n._(msg({ id: 'preview.finishing.title', message: 'Your preview is almost complete.' }))
            : i18n._(msg({ id: 'preview.paused.title', message: 'Tracking is paused.' }));
    const stateMessage = isLoading
        ? isPausing
            ? i18n._(msg({ id: 'preview.loadingWhileFinishing', message: 'Your strongest matches are ready to inspect now. We are loading their details while tracking finishes safely.' }))
            : i18n._(msg({ id: 'preview.loadingWhilePaused', message: 'Your one-time preview is complete and your project is not checking for new papers. We are loading the preview papers now.' }))
        : hasLoadError
            ? totalFoundCount > 0
                ? isPausing
                    ? i18n._({ ...msg({ id: 'preview.loadError.foundFinishing', message: 'We found {foundLabel}, but could not load their details right now. Tracking is finishing safely and will pause shortly.' }), values: { foundLabel } })
                    : i18n._({ ...msg({ id: 'preview.loadError.foundPaused', message: 'We found {foundLabel}, but could not load their details right now. Tracking is paused and your project is not checking for new papers.' }), values: { foundLabel } })
                : isPausing
                    ? i18n._(msg({ id: 'preview.loadError.emptyFinishing', message: 'We could not load the paper details from this one-time preview right now. Tracking is finishing safely and will pause shortly.' }))
                    : i18n._(msg({ id: 'preview.loadError.emptyPaused', message: 'We could not load the paper details from this one-time preview right now. Tracking is paused and your project is not checking for new papers.' }))
            : hasPapers
                ? isPausing
                    ? i18n._({ ...msg({ id: 'preview.results.finishing', message: 'We found {foundLabel} for “{projectName}”. This preview shows {previewScopeLabel}. You can inspect them now while tracking finishes safely and pauses.' }), values: { foundLabel, projectName, previewScopeLabel } })
                    : i18n._({ ...msg({ id: 'preview.results.paused', message: 'We found {foundLabel} for “{projectName}”. This preview shows {previewScopeLabel}. Tracking is paused and your project is not checking for new papers.' }), values: { foundLabel, projectName, previewScopeLabel } })
                : isPausing
                    ? i18n._(msg({ id: 'preview.empty.finishing', message: 'This one-time preview is finishing safely and tracking will pause shortly.' }))
                    : i18n._(msg({ id: 'preview.empty.paused', message: 'This one-time preview did not return a matching paper. Tracking is paused and your project is not checking for new papers.' }));

    return (
        <View style={styles.section} testID="project-preview-ready-section">
            <View
                style={[styles.readyCard, isNarrow && styles.readyCardNarrow]}
                testID="project-preview-ready-card"
            >
                <View style={styles.readyIcon}>
                    <FontAwesome6
                        name={hasPapers && !hasLoadError ? 'circle-check' : 'pause'}
                        size={24}
                        color={colors.primaryGreen}
                    />
                </View>
                <View style={styles.readyCopy}>
                    <Text style={[styles.eyebrow, interSemiBold]}>
                        {isPausing ? <Trans id="digest.projectpreviewreadysection.oneTimePreviewReady" comment="Conditional UI copy in components/digest/ProjectPreviewReadySection.tsx">ONE-TIME PREVIEW READY</Trans> : <Trans id="digest.projectpreviewreadysection.oneTimePreviewComplete" comment="Conditional UI copy in components/digest/ProjectPreviewReadySection.tsx">ONE-TIME PREVIEW COMPLETE</Trans>}
                    </Text>
                    <Text
                        style={[styles.readyTitle, interSemiBold]}
                        accessibilityRole="header"
                        {...(Platform.OS === 'web' && { role: 'heading', 'aria-level': 2 })}
                    >
                        {stateTitle}
                    </Text>
                    <Text style={[styles.readyBody, interRegular]}>
                        {stateMessage}
                    </Text>
                    <PreviewResumeTrackingButton
                        previewProjectId={projectId}
                        resultDigestId={resultDigestId}
                        testID="preview-resume-tracking-button-top"
                        label={isPausing
                            ? msg({ id: 'preview.keepTrackingTrial', message: 'Keep tracking with a free trial' })
                            : undefined}
                    />
                </View>
            </View>

            {isLoading ? (
                <View style={styles.loadingRow} testID="project-preview-paper-state-loading">
                    <ActivityIndicator size="small" color={colors.primaryGreen} />
                    <Text style={[styles.loadingText, interRegular]}><Trans id="digest.projectpreviewreadysection.loadingPreviewPapers" comment="UI copy in components/digest/ProjectPreviewReadySection.tsx">Loading preview papers...</Trans></Text>
                </View>
            ) : null}

            {hasLoadError ? (
                <View style={styles.loadError} testID="project-preview-paper-state-error">
                    <Text style={[styles.loadErrorText, interRegular]}><Trans id="digest.projectpreviewreadysection.paperDetailsAreTemporarilyUnavailableYouCanStillResumeTracking" comment="UI copy in components/digest/ProjectPreviewReadySection.tsx">
                        Paper details are temporarily unavailable. You can still resume tracking below.
                    </Trans></Text>
                </View>
            ) : null}

            {hasPapers && !hasLoadError ? (
                <>
                    <View style={styles.previewHeader}>
                        <View style={styles.previewHeaderCopy}>
                            <Text style={[styles.eyebrow, interSemiBold]}><Trans id="digest.projectpreviewreadysection.oneTimePreview" comment="UI copy in components/digest/ProjectPreviewReadySection.tsx">ONE-TIME PREVIEW</Trans></Text>
                            <Text style={[styles.previewTitle, interSemiBold]}><Trans id="digest.projectpreviewreadysection.firstMatchesFor" comment="UI copy in components/digest/ProjectPreviewReadySection.tsx">
                                First matches for “{projectName}”
                            </Trans></Text>
                            <Text style={[styles.previewBody, interRegular]}><Trans id="digest.projectpreviewreadysection.showingOfOpenAnyPaperToInspectIt" comment="UI copy in components/digest/ProjectPreviewReadySection.tsx">
                                Showing {shownCount} of {foundLabel}. Open any paper to inspect it.
                            </Trans></Text>
                        </View>
                        <Link href={feedHref} asChild>
                            <PaperFeedButton
                                variant="secondary"
                                testID="project-preview-paper-feed-button"
                            />
                        </Link>
                    </View>

                    <View style={styles.paperList}>
                        {papers.map((item) => (
                            <PaperCard
                                key={item.id}
                                paper={item.paper}
                                linkHref={getFeedPaperHref(item, projectId, pathname)}
                                paperzillaTitle={item.paper_title}
                                showScores
                                relevanceScore={item.relevance_score}
                                relevanceClass={item.relevance_class}
                                showSummary
                                summaryContent={item.summary || item.paper.abstract}
                            />
                        ))}
                    </View>

                    <PreviewHiddenMatchesNotice
                        totalFoundCount={totalFoundCount}
                        shownCount={shownCount}
                        previewProjectId={projectId}
                        resultDigestId={resultDigestId}
                    />
                </>
            ) : !isLoading && !hasLoadError ? (
                <View style={styles.emptyState} testID="project-preview-paper-state-empty">
                    <Text style={[styles.emptyStateText, interRegular]}><Trans id="digest.projectpreviewreadysection.noPaperCardsAreAvailableInThisPreview" comment="UI copy in components/digest/ProjectPreviewReadySection.tsx">
                        No paper cards are available in this preview.
                    </Trans></Text>
                </View>
            ) : null}

            <PreviewTrackingCard
                projectName={projectName}
                previewProjectId={projectId}
                resultDigestId={resultDigestId}
                trackingState={trackingState}
            />
        </View>
    );
}

const styles = StyleSheet.create({
    section: {
        width: '100%',
        gap: 22,
        marginBottom: 28,
    },
    readyCard: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 16,
        paddingHorizontal: 24,
        paddingVertical: 24,
        borderRadius: 18,
        borderWidth: 1,
        borderColor: '#d5e6c7',
        backgroundColor: '#f4f9ef',
    },
    readyCardNarrow: {
        flexDirection: 'column',
        gap: 14,
        paddingHorizontal: 20,
        paddingVertical: 22,
    },
    readyIcon: {
        width: 44,
        height: 44,
        borderRadius: 22,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#e2efd7',
        flexShrink: 0,
    },
    readyCopy: {
        flex: 1,
        alignItems: 'flex-start',
        minWidth: 0,
    },
    eyebrow: {
        color: colors.primaryGreen,
        fontSize: 11,
        lineHeight: 16,
        letterSpacing: 0.7,
        marginBottom: 5,
    },
    readyTitle: {
        color: colors.text900,
        fontSize: 26,
        lineHeight: 33,
        marginBottom: 8,
    },
    readyBody: {
        color: colors.text700,
        fontSize: 15,
        lineHeight: 23,
        maxWidth: 720,
    },
    previewHeader: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 18,
    },
    previewHeaderCopy: {
        flex: 1,
        minWidth: 0,
    },
    previewTitle: {
        color: colors.primaryGreenDark,
        fontSize: 22,
        lineHeight: 29,
        marginBottom: 5,
    },
    previewBody: {
        color: colors.text600,
        fontSize: 14,
        lineHeight: 21,
    },
    paperList: {
        gap: 14,
    },
    loadingRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingHorizontal: 16,
        paddingVertical: 14,
        borderRadius: 12,
        backgroundColor: colors.card,
        borderWidth: 1,
        borderColor: colors.border,
    },
    loadingText: {
        color: colors.text600,
        fontSize: 14,
        lineHeight: 20,
    },
    loadError: {
        paddingHorizontal: 16,
        paddingVertical: 14,
        borderRadius: 12,
        backgroundColor: '#fff7ed',
        borderWidth: 1,
        borderColor: '#fed7aa',
    },
    loadErrorText: {
        color: '#9a3412',
        fontSize: 14,
        lineHeight: 20,
    },
    emptyState: {
        paddingHorizontal: 16,
        paddingVertical: 14,
        borderRadius: 12,
        backgroundColor: colors.card,
        borderWidth: 1,
        borderColor: colors.border,
    },
    emptyStateText: {
        color: colors.text600,
        fontSize: 14,
        lineHeight: 20,
    },
});
